/**
 * Lens Cards Service
 *
 * Service for loading and managing lens cards from the song_lens_cards table.
 * Uses the refactored lens system with enriched view.
 * Provides the 3 lens cards displayed in Explorer's One Key Insight modal.
 */

import { supabase } from './supabaseClient';
import { getSongInsights, SongInsightMatch } from './insightArchetypesService';

export interface LensCard {
  id: string;
  song_id: string;
  civic_level: string;
  slot: number;
  lens_id: string;
  back_insight: string | null;
  source: 'auto' | 'manual';
  lens_key: string;
  lens_label: string;
  lens_description: string;
}

/**
 * Load lens cards for a song using the enriched view
 * Falls back to auto-generated insights if no cards exist
 */
export async function getLensCards(songId: string): Promise<LensCard[]> {
  try {
    // Load from enriched view (includes fallback logic for subtitle)
    const { data: cards, error } = await supabase
      .from('v_song_lens_cards_enriched')
      .select('*')
      .eq('song_id', songId)
      .eq('civic_level', 'explorer')
      .order('slot');

    if (error) {
      console.error('[getLensCards] Database error:', error);
      throw error;
    }

    // If we have cards, return them
    if (cards && cards.length > 0) {
      console.log('[getLensCards] Loaded', cards.length, 'cards from enriched view');
      return cards;
    }

    // If no cards exist, fall back to auto-generated insights
    console.log('[getLensCards] No cards found, falling back to auto-generated insights');
    const insights = await getSongInsights(songId);

    // Convert insights to lens card format (take top 3)
    return insights.slice(0, 3).map((insight, index) => ({
      id: `temp-${index}`,
      song_id: songId,
      civic_level: 'explorer',
      slot: index + 1,
      lens_id: insight.archetype.id,
      back_insight: insight.anchor_text || insight.why_it_matters || insight.archetype.insight_text,
      source: 'auto' as const,
      lens_key: insight.archetype.category.toUpperCase(),
      lens_label: insight.archetype.category,
      lens_description: insight.archetype.insight_text,
    }));
  } catch (err) {
    console.error('[getLensCards] Error:', err);
    throw err;
  }
}

/**
 * Convert a lens card to the SongInsightMatch format for compatibility with existing UI
 * Uses new lens system fields (lens_label, back_insight)
 */
export function lensCardToInsightMatch(card: LensCard): SongInsightMatch {
  return {
    id: card.id,
    song_id: card.song_id,
    archetype_id: card.lens_id,
    display_order: card.slot,
    is_auto_generated: card.source === 'auto',
    anchor_text: card.back_insight || card.lens_description,
    why_it_matters: card.back_insight || '',
    archetype: {
      id: card.lens_id,
      category: card.lens_key.toLowerCase(),
      insight_text: card.lens_description,
      theme_tags: [],
    },
  };
}

/**
 * Get lens cards as SongInsightMatch array for compatibility with existing UI
 */
export async function getLensCardsAsInsights(songId: string): Promise<SongInsightMatch[]> {
  const cards = await getLensCards(songId);
  return cards.map(lensCardToInsightMatch);
}
